// Автообновление страницы после выхода новой версии фронта.
// Каждая сборка называет файлы по хешу, поэтому новая версия видна по списку скриптов в index.html (он отдаётся без кэша).
// Нашли отличие — показываем плашку «Вышла новая версия»; страница перезагружается сама: при следующем переходе
// между разделами, а если пользователь ничего не делает — через минуту простоя (набираемый текст не теряем).
// Устаревший чанк после выката (ошибка загрузки раздела) тоже лечится перезагрузкой.
import Vue from 'vue'

// available — новая версия вышла и ждёт перезагрузки страницы.
export const versionState = Vue.observable({ available: false })

const CHECK_MS = 2 * 60 * 1000
const IDLE_MS = 60 * 1000
// Запрос списка файлов не должен висеть: иначе флаг «проверяем» остался бы навсегда и новые проверки не запускались бы
const FETCH_TIMEOUT_MS = 20 * 1000
// Не чаще раза в 30 секунд на переходах между разделами
const ROUTE_CHECK_GAP_MS = 30 * 1000
const GUARD_KEY = 'app-version-reload-at'

let lastActivity = Date.now()
let checking = false
let lastCheckAt = 0

// Скрипты приложения (файлы с хешем в /js/): по ним версии и различаются.
function signature(html) {
  const found = []
  const re = /<script[^>]+src=["']([^"']*\/js\/[^"']+)["']/g
  let m
  while ((m = re.exec(html)) !== null) found.push(m[1])
  return found.sort().join('|')
}

function currentSignature() {
  return Array.from(document.querySelectorAll('script[src*="/js/"]'))
    .map((el) => el.getAttribute('src'))
    .sort()
    .join('|')
}

function editing() {
  const el = document.activeElement
  return !!el && ['INPUT', 'TEXTAREA', 'SELECT'].indexOf(el.tagName) !== -1
}

// Не чаще раза в 30 секунд: если версия не подтянулась (кэш прокси), не уходим в цикл перезагрузок.
function reloadAllowed() {
  try {
    const last = Number(sessionStorage.getItem(GUARD_KEY) || 0)
    if (Date.now() - last < 30 * 1000) return false
    sessionStorage.setItem(GUARD_KEY, String(Date.now()))
  } catch (e) {
    // без sessionStorage перезагружаем как обычно
  }
  return true
}

export function reloadNow(url) {
  if (!reloadAllowed()) return
  if (url) window.location.assign(url)
  else window.location.reload()
}

function check() {
  if (checking || versionState.available) return Promise.resolve()
  checking = true
  lastCheckAt = Date.now()
  // без AbortController (очень старые браузеры) проверка идёт как раньше, просто без ограничения по времени
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS) : null
  return fetch('/index.html?_=' + Date.now(), {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: { 'Cache-Control': 'no-cache' },
    signal: controller ? controller.signal : undefined
  })
    .then((res) => (res.ok ? res.text() : ''))
    .then((html) => {
      const latest = signature(html)
      if (latest && latest !== currentSignature()) versionState.available = true
    })
    .catch(() => {
      // нет сети — проверим в следующий раз
    })
    .finally(() => {
      clearTimeout(timer)
      checking = false
    })
}

export function setupVersionCheck(router) {
  // В разработке (npm run serve) файлы без хешей — проверять нечего.
  if (process.env.NODE_ENV !== 'production') return

  ;['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'].forEach((name) => {
    window.addEventListener(name, () => {
      lastActivity = Date.now()
    }, { passive: true, capture: true })
  })

  setInterval(check, CHECK_MS)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) check()
  })
  window.addEventListener('focus', check)
  // Связь появилась после обрыва — сразу смотрим, не вышла ли новая версия
  window.addEventListener('online', check)
  // На телефоне таймеры в фоне замирают: проверяем ещё и при переходах между разделами
  router.afterEach(() => {
    if (Date.now() - lastCheckAt > ROUTE_CHECK_GAP_MS) check()
  })
  // Для проверки руками: в консоли браузера STR_VERSION.check() и STR_VERSION.state
  window.STR_VERSION = { check, state: versionState }

  // Простой: никто ничего не делает и ничего не набирает — обновляемся.
  setInterval(() => {
    if (versionState.available && !document.hidden && !editing() && Date.now() - lastActivity > IDLE_MS) reloadNow()
  }, 15 * 1000)

  // Переход в другой раздел при вышедшей версии — сразу на новую.
  router.beforeEach((to, from, next) => {
    if (versionState.available && from.name !== null && reloadAllowed()) {
      window.location.assign(to.fullPath)
      return
    }
    next()
  })

  // Раздел не загрузился из-за устаревшего чанка: версия уже сменилась.
  router.onError((err) => {
    const text = String((err && (err.name + ' ' + err.message)) || '')
    if (/ChunkLoadError|Loading chunk|Loading CSS chunk|Failed to fetch dynamically imported/i.test(text)) reloadNow()
  })

  setTimeout(check, 10 * 1000)
}
